import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useMemo, useState, type ReactNode } from 'react';
import {
  formatRobotShortId,
  type InventoryKanbanGroupDto,
  type InventoryKanbanResponse,
  type InventoryKanbanRobotDto,
} from '@bhops/shared';
import { teamDashboardApi } from '@/api/team-dashboard';
import {
  AutoRefreshHint,
  DashboardError,
  DashboardSkeleton,
  SHARED_CARD,
  SHARED_CARD_TITLE,
  SHARED_EMPTY_HINT,
  SHARED_REFETCH_INTERVAL_MS,
} from '@/pages/dashboard/_shared';

// ============================================================
// 재고 현황 — S2 Phase 2.10 (spec §1.6 시트4/5)
//
// 완제품 창고 재고. 서버는 구분 사양(모델 × 색상 × 구분사양)별로 세 단계 버킷을 준다.
// stage 체계: 엑셀 = 공정진행 → 검사 → 출고미확정 → 출고확정.
// '검사(QC)' 전용 Robot.status 가 없어 검사를 공정진행에 fold 한 3-버킷.
// 매핑은 backend inventory.service.ts + api-contracts.ts InventoryKanbanStage 주석 참조.
//
// 2026-11-03 오너 — "리스팅 해둔게 눈에 안 들어옴", "초록 동그라미가 뭘 뜻하는지 이해가 안 감",
// "ui들이 전반적으로 눈에 잘 들어오지 않음", "디자인 아예 새로". 시안 여러 차례 끝에 '큰 숫자 포스터'로.
//   · 첫눈에 보일 것 = 사양별 '출고 가능'(출고미확정) 대수. 모델마다 진한 색 띠, 사양마다 아주 큰 숫자 하나.
//   · 번호는 처음부터 깔지 않는다. 사양을 누르면 띠 아래 흰 판에 출고 가능 · 출고확정(행선지) · 공정진행 번호.
//   · '신규' 점은 없앴다(재고는 거의 다 신규라 뜻이 없었다). 대신 출고됐다가 다시 들어온 로봇에 '재입고'를 글로.
//   · 불감지 내역은 사양 이름 아래(오너 "3번에도 이런 정보가 들어가게").
// 모양은 globals.css 의 inv-* 규칙에 있다.
// ============================================================

type Robot = InventoryKanbanRobotDto;
type Group = InventoryKanbanGroupDto;

const avail = (g: Group) => g.buckets.readyUnassigned;
const assigned = (g: Group) => g.buckets.readyAssigned;
const making = (g: Group) => g.buckets.inProcess;

// round5 #12 — 그룹 축이 3축(모델×색상×구분사양)으로 줄며 불감지는 라벨에서 빠지고 sensorTypeBreakdown 보조 표기로.
function specLabel(g: Group): string {
  const parts = [g.color, g.variantValue].filter((x): x is string => !!x && x.length > 0);
  // 다 비었으면 이름 자리에 뭐가 빠졌는지를 쓴다(아래 '미입력' 꼬리표는 일부만 빈 경우에만).
  return parts.length > 0 ? parts.join(' ') : (missingLabel(g) ?? '사양 미입력');
}

/** 로봇에 비어 있는 사양 칸 (예: "색상, 방향 미입력"). 오너 "여기 왜 스펙이 제대로 안보이냐" — 뭐가 빠졌는지 말한다. */
function missingLabel(g: Group): string | null {
  const missing = [
    !g.color ? '색상' : null,
    !g.variantValue ? (g.variantAxisLabel ?? '구분 사양') : null,
  ].filter((x): x is string => !!x);
  return missing.length > 0 ? `${missing.join(', ')} 미입력` : null;
}

// 불감지 내역 (예: "불감지 F14 N2"). 비어 있는 로봇은 '미입력'으로 센다.
function sensorLabel(g: Group): string | null {
  const all = Object.entries(g.sensorTypeBreakdown ?? {});
  const unknown = all.filter(([k]) => k === '미지정').reduce((s, [, n]) => s + n, 0);
  const known = all.filter(([k]) => k !== '미지정').sort(([a], [b]) => a.localeCompare(b));
  if (known.length === 0) return unknown > 0 ? '불감지 미입력' : null;
  const head = `불감지 ${known.map(([k, n]) => `${k}${n}`).join(' ')}`;
  return unknown > 0 ? `${head}, 미입력 ${unknown}` : head;
}

/** 번호 — 화면엔 숫자만(#은 뺀다), 이름은 '#번호'. */
function snOf(r: Robot): { label: string; text: string } {
  if (!r.serialShort) return { label: r.serialLong ?? '–', text: r.serialLong ?? '–' };
  const label = formatRobotShortId(r.serialShort);
  return { label, text: label.replace(/^#/, '') };
}

const byNumber = (l: Robot[]) =>
  [...l].sort((a, b) => snOf(a).text.localeCompare(snOf(b).text, undefined, { numeric: true }));
/** 신규 먼저, 재입고는 뒤로 모은다. */
const freshFirst = (l: Robot[]) => [
  ...byNumber(l.filter((r) => r.isNew)),
  ...byNumber(l.filter((r) => !r.isNew)),
];

// 모델 띠 색 — 알려진 모델은 고정, 그 밖은 차례로.
const BAND_BY_MODEL: Record<string, string> = { GEN2: 'inv-band-teal', ZEPHYR: 'inv-band-ink' };
const BAND_CYCLE = ['inv-band-teal', 'inv-band-ink', 'inv-band-slate', 'inv-band-plum'];

interface ModelRollup {
  modelCode: string;
  modelDisplayName: string;
  available: number;
  groups: Group[];
}

/** 서버 group key 는 그대로 두고(테스트 · e2e 계약) 화면에서만 모델로 묶는다. 사양은 출고 가능이 많은 순. */
function rollupByModel(groups: Group[]): ModelRollup[] {
  const map = new Map<string, ModelRollup>();
  for (const g of groups) {
    let m = map.get(g.modelCode);
    if (!m) {
      m = {
        modelCode: g.modelCode,
        modelDisplayName: g.modelDisplayName,
        available: 0,
        groups: [],
      };
      map.set(g.modelCode, m);
    }
    m.groups.push(g);
    m.available += avail(g).length;
  }
  return Array.from(map.values()).map((m) => ({
    ...m,
    groups: [...m.groups].sort((a, b) => avail(b).length - avail(a).length),
  }));
}

export function InventoryKanbanPage() {
  const q = useQuery({
    queryKey: ['dashboard', 'inventory-kanban'],
    queryFn: () => teamDashboardApi.inventoryKanban(),
    staleTime: 60_000,
    refetchInterval: SHARED_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
  const models = useMemo(() => rollupByModel(q.data?.groups ?? []), [q.data?.groups]);
  // 펼친 사양 하나(group key). 다시 누르면 접힌다.
  const [openKey, setOpenKey] = useState<string | null>(null);

  if (q.isLoading) return <DashboardSkeleton kpiCount={3} />;
  if (q.error) return <DashboardError onRetry={q.refetch} />;
  const d = q.data!;

  return (
    <div className="inv-page">
      <Summary d={d} />

      {d.groups.length === 0 ? (
        <div style={SHARED_CARD}>
          <div style={SHARED_CARD_TITLE}>재고 현황</div>
          <div style={SHARED_EMPTY_HINT}>완제품 창고 재고가 없습니다.</div>
        </div>
      ) : (
        models.map((m, i) => {
          const picked = m.groups.find((g) => g.key === openKey);
          const band = BAND_BY_MODEL[m.modelCode] ?? BAND_CYCLE[i % BAND_CYCLE.length];
          return (
            <section key={m.modelCode} className={`inv-band ${band}`} data-testid="kanban-model">
              <div className="inv-band-head" data-testid={`kanban-model-header-${m.modelCode}`}>
                <h2>{m.modelDisplayName}</h2>
                <span>{m.available}대 출고 가능</span>
              </div>
              <div className="inv-specs">
                {m.groups.map((g) => {
                  const open = openKey === g.key;
                  const n = avail(g).length;
                  const sensors = sensorLabel(g);
                  return (
                    <button
                      key={g.key}
                      type="button"
                      className={open ? 'inv-spec on' : 'inv-spec'}
                      onClick={() => setOpenKey(open ? null : g.key)}
                      aria-expanded={open}
                      data-testid="kanban-group"
                    >
                      {/* 이름 · 미입력 꼬리표는 한 줄, 불감지 줄은 늘 자리를 잡는다 — 큰 숫자가 칸마다 같은 높이에 선다. */}
                      <span className="inv-spec-title">
                        <span className="inv-spec-name">{specLabel(g)}</span>
                        {(g.color || g.variantValue) && missingLabel(g) && (
                          <span className="inv-spec-missing" data-testid="kanban-spec-missing">
                            {missingLabel(g)}
                          </span>
                        )}
                      </span>
                      <span className="inv-spec-sensor" data-testid="kanban-sensor-breakdown">
                        {sensors ?? <>&nbsp;</>}
                      </span>
                      <span
                        className={n ? 'inv-spec-n' : 'inv-spec-n zero'}
                        data-testid="kanban-count-readyUnassigned"
                      >
                        {n}
                      </span>
                      <span className="inv-spec-sub">
                        <span data-testid="kanban-count-readyAssigned">
                          출고확정<b>{assigned(g).length}</b>
                        </span>
                        <span data-testid="kanban-count-inProcess">
                          공정진행<b>{making(g).length}</b>
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
              {picked && <SpecSheet g={picked} />}
            </section>
          );
        })
      )}

      <AutoRefreshHint isFetching={q.isFetching} />
    </div>
  );
}

/**
 * 맨 위 한 줄 — 지금 출고 가능 대수를 크게, 나머지 단계와 정비 중은 그 옆에.
 * 개선계획 §3/§7 — '정비 중 N대'. 회수가 정비 입고로 통일되며 회수 로봇이 재고에서 빠졌다 —
 * 이 줄이 "그럼 어디 갔지"에 답한다. 누르면 로봇 목록의 정비 상태 필터로 간다.
 */
function Summary({ d }: { d: InventoryKanbanResponse }) {
  const stale = d.maintenanceStaleCount > 0;
  // 2026-11-03 오너 "따로 블럭으로" → "지금 페이지랑 좀 어울리는 블럭 형태로" — 아래 모델 띠와 같은 말투:
  // 흰 판 한 장(오너가 고른 후보 ①), 가는 선으로 나눈 칸, 큰 숫자와 옅은 이름표. 출고 가능만 청록.
  return (
    <header className="inv-hero">
      <div className="inv-hero-cell lead">
        <span className="inv-hero-label">지금 출고할 수 있는 로봇</span>
        <span className="inv-hero-n">
          <b data-testid="kanban-total-readyUnassigned">{d.stageTotals.readyUnassigned}</b>대
        </span>
      </div>
      <div className="inv-hero-cell">
        <span className="inv-hero-label">출고확정</span>
        <span className="inv-hero-n">
          <b data-testid="kanban-total-readyAssigned">{d.stageTotals.readyAssigned}</b>대
        </span>
      </div>
      <div className="inv-hero-cell">
        <span className="inv-hero-label">공정진행</span>
        <span className="inv-hero-n">
          <b data-testid="kanban-total-inProcess">{d.stageTotals.inProcess}</b>대
        </span>
      </div>
      <Link
        to="/manufacturing/robots?status=IN_AS"
        className={stale ? 'inv-hero-cell link stale' : 'inv-hero-cell link'}
        data-testid="kanban-total-maintenance"
        title={
          stale
            ? `정비 입고 후 30일이 지난 로봇이 ${d.maintenanceStaleCount}대입니다. 수리가 멈춰 있는지 확인하세요.`
            : '정비 중인 로봇 목록 보기'
        }
      >
        <span className="inv-hero-label">정비 중</span>
        <span className="inv-hero-n">
          <b>{d.maintenanceCount}</b>대
        </span>
        {stale && (
          <span className="inv-hero-warn" data-testid="kanban-maintenance-stale">
            30일 넘은 {d.maintenanceStaleCount}대
          </span>
        )}
      </Link>
      <div className="inv-hero-cell">
        <span className="inv-hero-label">재고 로봇 전체</span>
        <span className="inv-hero-n">
          <b>{d.totalRobots}</b>대
        </span>
      </div>
    </header>
  );
}

/** 사양을 누르면 띠 아래에 펼쳐지는 흰 판 — 출고 가능 · 출고확정(행선지) · 공정진행 번호. */
function SpecSheet({ g }: { g: Group }) {
  const part = (title: string, n: number, children: ReactNode) => (
    <div className="inv-part">
      <h3>
        {title}
        <b>{n}대</b>
      </h3>
      {children}
    </div>
  );
  const none = <span className="inv-none">없음</span>;
  return (
    <div className="inv-sheet" data-testid="kanban-group-detail">
      {part(
        '출고 가능',
        avail(g).length,
        avail(g).length ? (
          <div className="inv-nos">
            {freshFirst(avail(g)).map((r) => (
              <RobotNo key={r.robotId} r={r} />
            ))}
          </div>
        ) : (
          none
        ),
      )}
      {part(
        '출고확정',
        assigned(g).length,
        assigned(g).length ? (
          <ul className="inv-dest">
            {byNumber(assigned(g)).map((r) => (
              <li key={r.robotId}>
                <RobotNo r={r} />
                <span data-testid="kanban-chip-destination">
                  {r.reservedCustomerDisplayName ?? '고객사 미정'}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          none
        ),
      )}
      {part(
        '공정진행',
        making(g).length,
        making(g).length ? (
          <div className="inv-nos">
            {byNumber(making(g)).map((r) => (
              <RobotNo key={r.robotId} r={r} />
            ))}
          </div>
        ) : (
          none
        ),
      )}
    </div>
  );
}

function RobotNo({ r }: { r: Robot }) {
  const sn = snOf(r);
  return (
    <Link
      to={`/robots/${r.robotId}`}
      className={r.isNew ? 'inv-no' : 'inv-no back'}
      aria-label={r.isNew ? sn.label : `${sn.label} 재입고`}
      title={r.serialLong ?? sn.label}
      data-testid="kanban-sn-chip"
    >
      {sn.text}
      {!r.isNew && (
        <small aria-hidden data-testid="kanban-returned">
          재입고
        </small>
      )}
    </Link>
  );
}
